/**
 * Video Editor — Surface 2 conversational brief (Phase 4).
 *
 * Replaces the plain-text brief: Fanny chats, the co-pilot extracts brief
 * fields from the conversation, and the brief fills. Wired to the agent
 * substrate via POST /projects/:id/copilot/turn (surface S2).
 */
import React, { useState } from 'react';
import type { VideoProject } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';

interface ChatMsg { role: 'user' | 'bot'; text: string; }
interface Chip { field: string; label: string; value: any; }

interface Props {
  project: VideoProject;
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
}

const BRIEF_ORDER = ['vibe', 'length_target_s', 'brand', 'hook', 'avoid', 'music'];

const briefObj = (b: any) =>
  (b && typeof b === 'object' && !Array.isArray(b)) ? b : {};

const ConversationalBrief: React.FC<Props> = ({ project, api, onRefresh }) => {
  const [messages, setMessages] = useState<ChatMsg[]>([
    { role: 'bot', text: "Tell me about the video — the vibe, how long, which brand. I'll put the brief together as we talk." }
  ]);
  const [input, setInput] = useState('');
  const [chips, setChips] = useState<Chip[]>([]);
  const [brief, setBrief] = useState<any>(briefObj(project.brief));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send(text: string) {
    const t = text.trim();
    if (!t || busy) return;
    const history = messages.map(m => ({
      role: m.role === 'bot' ? 'assistant' : 'user', text: m.text
    }));
    setMessages(m => [...m, { role: 'user', text: t }]);
    setInput(''); setChips([]); setBusy(true); setErr(null);
    try {
      const res = await api.copilotTurn(project._id, 'S2', t, history);
      const out = res.output || {};
      setMessages(m => [...m, { role: 'bot', text: out.message || '(no reply)' }]);
      if (Array.isArray(out.chip_options)) setChips(out.chip_options);
      if (res.brief) setBrief(briefObj(res.brief));
      if (res.validation && res.validation.status !== 'passed') {
        setErr('That reply was routed to review by the validation gate.');
      }
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Co-pilot turn failed');
      setMessages(m => [...m, { role: 'bot', text: '(the co-pilot hit an error)' }]);
    } finally { setBusy(false); }
  }

  const filled = BRIEF_ORDER.filter(f => brief[f] != null && brief[f] !== '');

  return (
    <div className="ve-copilot-s2">
      <h3>Brief co-pilot</h3>
      {err && <div className="ve-banner ve-banner--error">{err}</div>}

      <div className="ve-chat">
        {messages.map((m, i) => (
          <div key={i} className={`ve-msg ve-msg--${m.role}`}>{m.text}</div>
        ))}
        {busy && <div className="ve-msg ve-msg--bot ve-muted">thinking…</div>}
      </div>

      {chips.length > 0 && (
        <div className="ve-chips">
          {chips.map((c, i) => (
            <button key={i} className="ve-chip" disabled={busy}
              onClick={() => send(`${c.field}: ${c.value}`)}>{c.label}</button>
          ))}
        </div>
      )}

      <div className="ve-chat-input">
        <input
          className="ve-textinput"
          value={input}
          disabled={busy}
          placeholder="Describe the video…"
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(input); }}
        />
        <button className="ve-btn ve-btn--primary" disabled={busy || !input.trim()}
          onClick={() => send(input)}>Send</button>
      </div>

      <h3 style={{ marginTop: 'var(--space-16)' }}>
        Brief so far <span className="ve-muted">({filled.length}/{BRIEF_ORDER.length})</span>
      </h3>
      {filled.length === 0 ? (
        <p className="ve-muted">Nothing captured yet — the chat fills this in.</p>
      ) : filled.map(f => (
        <div className="ve-cliprow" key={f}>
          <span className="ve-cliprow__name">{f.replace(/_/g, ' ')}</span>
          <span className="ve-muted">{String(brief[f])}</span>
        </div>
      ))}
    </div>
  );
};

export default ConversationalBrief;
